import React, { useState, useEffect } from 'react';
import { db, handleFirestoreError, OperationType } from '../../lib/firebase';
import { collection, addDoc, writeBatch, doc, getDocs, onSnapshot } from 'firebase/firestore';
import { ConventionMetadata, Prop } from '../../types';
import { Input } from '../ui/input';
import { format, eachDayOfInterval, parseISO, isBefore, isAfter, isEqual } from 'date-fns';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, Save, Calendar, Clock, MapPin, Globe, Phone, Layers, Users, Radio, Info } from 'lucide-react';
import { Textarea } from '../ui/textarea';

interface ConWizardProps {
  onComplete: () => void;
  currentUserUid: string;
}

export default function ConWizard({ onComplete, currentUserUid }: ConWizardProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState<Partial<ConventionMetadata>>({
    name: '',
    startDate: '',
    endDate: '',
    boothSize: '',
    address: '',
    website: '',
    helpContact: '',
    host: '',
    commFrequency: '',
    volunteerCommLink: '',
    photoAlbumLink: '',
    pertinentInfo: '',
    reservedPropIds: [],
    dailyHours: {},
    ownerId: currentUserUid,
    status: 'draft'
  });

  const [props, setProps] = useState<Prop[]>([]);
  const [activeConventions, setActiveConventions] = useState<ConventionMetadata[]>([]);
  const [unavailablePropIds, setUnavailablePropIds] = useState<string[]>([]);

  useEffect(() => {
    const unsubProps = onSnapshot(collection(db, 'props'), (snap) => {
      setProps(snap.docs.map(d => ({ id: d.id, ...d.data() } as Prop)));
    });

    const fetchCons = async () => {
      const snap = await getDocs(collection(db, 'conventions'));
      setActiveConventions(snap.docs.map(d => ({ id: d.id, ...d.data() } as ConventionMetadata)));
    };
    fetchCons();

    return () => unsubProps();
  }, []);

  const isValidDate = (d: string | undefined): boolean => d !== undefined && !isNaN(parseISO(d).getTime());

  const datesInRange = metadata.startDate && metadata.endDate && isValidDate(metadata.startDate) && isValidDate(metadata.endDate)
    ? eachDayOfInterval({ start: parseISO(metadata.startDate), end: parseISO(metadata.endDate) })
    : [];

  const checkAvailability = () => {
    if (!metadata.startDate || !metadata.endDate) return;
    
    const newStart = parseISO(metadata.startDate);
    const newEnd = parseISO(metadata.endDate);
    
    const unavailable = new Set<string>();
    
    activeConventions.forEach(con => {
      if (!con.startDate || !con.endDate) return;
      const conStart = parseISO(con.startDate);
      const conEnd = parseISO(con.endDate);
      
      // Check if dates overlap: newStart <= conEnd && newEnd >= conStart
      if (
         (isBefore(newStart, conEnd) || isEqual(newStart, conEnd)) && 
         (isAfter(newEnd, conStart) || isEqual(newEnd, conStart))
      ) {
         if (con.reservedPropIds) {
           con.reservedPropIds.forEach(id => unavailable.add(id));
         }
      }
    });
    
    setUnavailablePropIds(Array.from(unavailable));
    setStep(2);
  };

  const handleFinalize = async () => {
    if (!metadata.name || !metadata.startDate || !metadata.endDate) {
      toast.error('Tactical metadata incomplete');
      return;
    }
    setLoading(true);
    try {
      const dbData = Object.fromEntries(Object.entries(metadata).filter(([k, v]) => v !== undefined && k !== 'id'));
      await addDoc(collection(db, 'conventions'), {
        ...dbData,
        status: 'active',
        createdAt: new Date().toISOString()
      });

      toast.success('Convention recognized. Proceed to Deployment Core to generate shifts.');
      onComplete();
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'conventions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="flex items-center gap-4 mb-8">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-lcars-blue text-black' : 'bg-white/10 text-[var(--foreground)]/40'}`}>1</div>
        <div className="h-1 flex-1 bg-white/10"><div className={`h-full bg-lcars-blue transition-all ${step >= 2 ? 'w-full' : 'w-0'}`}></div></div>
        <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-lcars-blue text-black' : 'bg-white/10 text-[var(--foreground)]/40'}`}>2</div>
        <div className="h-1 flex-1 bg-white/10"><div className={`h-full bg-lcars-blue transition-all ${step === 3 ? 'w-full' : 'w-0'}`}></div></div>
        <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold ${step === 3 ? 'bg-lcars-blue text-black' : 'bg-white/10 text-[var(--foreground)]/40'}`}>3</div>
      </div>

      <div className="bg-white/5 border border-lcars-blue rounded-r-[40px] p-8 shadow-2xl">
        {step === 1 ? (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                 <h3 className="text-xl font-bold text-lcars-blue uppercase flex items-center gap-2 border-b border-lcars-blue/30 pb-2">
                   <Calendar className="w-5 h-5" /> Operation Parameters
                 </h3>
                 <div className="space-y-4">
                   <div className="space-y-1">
                     <label htmlFor="wiz-name" className="text-[0.6rem] text-lcars-blue font-bold uppercase ml-1">Name of Operation</label>
                     <Input 
                       id="wiz-name"
                       value={metadata.name} 
                       onChange={e => setMetadata({...metadata, name: e.target.value})}
                       className="bg-[var(--background)]/50 border-lcars-blue text-[var(--foreground)] focus:ring-1 focus:ring-lcars-blue" 
                     />
                   </div>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div className="space-y-1">
                       <label htmlFor="wiz-start" className="text-[0.6rem] text-lcars-blue font-bold uppercase ml-1">Start Date</label>
                       <Input 
                         id="wiz-start"
                         type="date" 
                         value={metadata.startDate} 
                         onChange={e => setMetadata({...metadata, startDate: e.target.value})}
                         className="bg-[var(--background)]/50 border-lcars-blue text-[var(--foreground)]" 
                       />
                     </div>
                     <div className="space-y-1">
                       <label htmlFor="wiz-end" className="text-[0.6rem] text-lcars-blue font-bold uppercase ml-1">End Date</label>
                       <Input 
                         id="wiz-end"
                         type="date" 
                         value={metadata.endDate} 
                         onChange={e => setMetadata({...metadata, endDate: e.target.value})}
                         className="bg-[var(--background)]/50 border-lcars-blue text-[var(--foreground)]" 
                       />
                     </div>
                   </div>
                   <div className="space-y-1">
                     <label htmlFor="wiz-host" className="text-[0.6rem] text-lcars-blue font-bold uppercase ml-1 flex items-center gap-1">
                       <Users className="w-3 h-3" /> Mission Host / Commander
                     </label>
                     <Input 
                       id="wiz-host"
                       value={metadata.host} 
                       onChange={e => setMetadata({...metadata, host: e.target.value})}
                       className="bg-[var(--background)]/50 border-lcars-blue text-[var(--foreground)]" 
                       placeholder="e.g. Admiral Jane Doe"
                     />
                   </div>
                   <div className="space-y-1">
                     <label htmlFor="wiz-comm" className="text-[0.6rem] text-lcars-blue font-bold uppercase ml-1 flex items-center gap-1">
                       <Radio className="w-3 h-3" /> Comm Frequency (Contact)
                     </label>
                     <Input 
                       id="wiz-comm"
                       value={metadata.commFrequency} 
                       onChange={e => setMetadata({...metadata, commFrequency: e.target.value})}
                       className="bg-[var(--background)]/50 border-lcars-blue text-[var(--foreground)]" 
                       placeholder="email@example.com or phone"
                     />
                   </div>
                   <div className="space-y-1">
                     <label htmlFor="wiz-booth" className="text-[0.6rem] text-lcars-blue font-bold uppercase ml-1">Booth Dimensions</label>
                     <Input 
                       id="wiz-booth"
                       value={metadata.boothSize} 
                       onChange={e => setMetadata({...metadata, boothSize: e.target.value})}
                       placeholder="e.g. 10x20 Endcap"
                       className="bg-[var(--background)]/50 border-lcars-blue text-[var(--foreground)]" 
                     />
                   </div>
                 </div>
              </div>

              <div className="space-y-6">
                 <h3 className="text-xl font-bold text-lcars-gold uppercase flex items-center gap-2 border-b border-lcars-gold/30 pb-2">
                   <MapPin className="w-5 h-5" /> Tactical Logistics
                 </h3>
                 <div className="space-y-4">
                   <div className="space-y-1">
                     <label htmlFor="wiz-address" className="text-[0.6rem] text-lcars-gold font-bold uppercase ml-1">Coordinates (Address)</label>
                     <Input 
                       id="wiz-address"
                       value={metadata.address || ''} 
                       onChange={e => setMetadata({...metadata, address: e.target.value})}
                       className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)]" 
                     />
                   </div>
                   <div className="space-y-1">
                     <label htmlFor="wiz-website" className="text-[0.6rem] text-lcars-gold font-bold uppercase ml-1">Subspace Link (Website)</label>
                     <Input 
                       id="wiz-website"
                       value={metadata.website || ''} 
                       onChange={e => setMetadata({...metadata, website: e.target.value})}
                       className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)]" 
                     />
                   </div>
                   <div className="space-y-1">
                     <label htmlFor="wiz-volcomms" className="text-[0.6rem] text-lcars-gold font-bold uppercase ml-1 flex items-center gap-1">
                       <Radio className="w-3 h-3" /> Volunteer Comms Link
                     </label>
                     <Input 
                       id="wiz-volcomms"
                       value={metadata.volunteerCommLink || ''} 
                       onChange={e => setMetadata({...metadata, volunteerCommLink: e.target.value})}
                       className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)]" 
                       placeholder="e.g. Discord, Slack, WhatsApp group..."
                     />
                   </div>
                   <div className="space-y-1">
                     <label htmlFor="wiz-photo" className="text-[0.6rem] text-lcars-gold font-bold uppercase ml-1">Photo Album Link</label>
                     <Input 
                       id="wiz-photo"
                       value={metadata.photoAlbumLink || ''} 
                       onChange={e => setMetadata({...metadata, photoAlbumLink: e.target.value})}
                       className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)]" 
                       placeholder="e.g. Google Photos, iCloud link..."
                     />
                   </div>
                   <div className="space-y-1">
                     <label htmlFor="wiz-info" className="text-[0.6rem] text-lcars-gold font-bold uppercase ml-1 flex items-center gap-1">
                        <Info className="w-3 h-3" /> Pertinent Mission Info
                     </label>
                     <Textarea 
                       id="wiz-info"
                       value={metadata.pertinentInfo || ''} 
                       onChange={e => setMetadata({...metadata, pertinentInfo: e.target.value})}
                       className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)] min-h-[140px] text-xs" 
                       placeholder="Enter additional details, requirements, or mission notes..."
                     />
                   </div>
                 </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-white/10">
              <button 
                onClick={checkAvailability}
                className="lcars-pill bg-lcars-blue text-black flex items-center gap-2 px-8 py-3 font-bold uppercase transition-all hover:bg-white"
              >
                PROCEED TO SCENE RESERVATION <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="space-y-8">
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="flex items-center justify-between border-b border-lcars-gold/30 pb-2 mb-4">
                <h3 className="text-xl font-bold text-lcars-gold uppercase flex items-center gap-2">
                  <Layers className="w-5 h-5" /> Scene Reservation
                </h3>
                <div className="text-[0.65rem] text-lcars-gold uppercase font-bold px-2 py-1 bg-lcars-gold/10 rounded">
                  Checking Availability For: {metadata.startDate} TO {metadata.endDate}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                {props.length === 0 && (
                  <div className="col-span-2 text-center text-[var(--foreground)]/40 uppercase py-8 text-sm">
                    No scenes/props available in database.
                  </div>
                )}
                {props.map(prop => {
                  if (prop.name === 'General Convention Duty') return null; // Skip general duty, it's always available implicitly
                  const isUnavailable = unavailablePropIds.includes(prop.id!);
                  const isSelected = metadata.reservedPropIds?.includes(prop.id!);

                  return (
                    <div 
                      key={prop.id}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          if (isUnavailable) {
                            toast.error(`Set "${prop.name}" is already reserved by another overlapping convention.`);
                            return;
                          }
                          const current = metadata.reservedPropIds || [];
                          const updated = isSelected 
                            ? current.filter(id => id !== prop.id)
                            : [...current, prop.id!];
                          setMetadata({ ...metadata, reservedPropIds: updated });
                        }
                      }}
                      onClick={() => {
                        if (isUnavailable) {
                          toast.error(`Set "${prop.name}" is already reserved by another overlapping convention.`);
                          return;
                        }
                        const current = metadata.reservedPropIds || [];
                        const updated = isSelected 
                          ? current.filter(id => id !== prop.id)
                          : [...current, prop.id!];
                        setMetadata({ ...metadata, reservedPropIds: updated });
                      }}
                      className={`relative overflow-hidden rounded-xl border p-4 transition-all ${
                        isUnavailable 
                          ? 'border-lcars-red/30 bg-lcars-red/5 opacity-50 cursor-not-allowed'
                          : isSelected 
                            ? 'border-lcars-blue bg-lcars-blue/20 cursor-pointer' 
                            : 'border-white/10 bg-white/5 hover:border-white/30 cursor-pointer'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`font-bold uppercase text-sm ${isUnavailable ? 'text-lcars-red' : isSelected ? 'text-lcars-blue' : 'text-[var(--foreground)]'}`}>
                          {prop.name}
                        </span>
                        {isUnavailable && (
                          <span className="bg-lcars-red text-black text-[0.6rem] font-bold px-2 py-0.5 rounded uppercase">
                            Conflict
                          </span>
                        )}
                        {isSelected && !isUnavailable && (
                          <span className="bg-lcars-blue text-black text-[0.6rem] font-bold px-2 py-0.5 rounded uppercase">
                            Reserved
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--foreground)]/60 line-clamp-2 leading-relaxed">
                        {prop.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between items-center pt-8 border-t border-white/10">
              <button 
                onClick={() => setStep(1)}
                className="lcars-pill bg-lcars-tan text-black flex items-center gap-2 px-8 py-3 font-bold uppercase hover:bg-white transition-all"
              >
                <ChevronLeft className="w-5 h-5" /> OPERATION PARAMETERS
              </button>
              <button 
                onClick={() => setStep(3)}
                className="lcars-pill bg-lcars-blue text-black flex items-center gap-2 px-8 py-3 font-bold uppercase transition-all hover:bg-white"
              >
                PROCEED TO TEMPORAL ALIGNMENT <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="max-w-xl mx-auto space-y-6">
              <h3 className="text-xl font-bold text-lcars-gold uppercase flex items-center gap-2 border-b border-lcars-gold/30 pb-2 mb-4">
                <Clock className="w-5 h-5" /> Opening Frequencies
              </h3>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                {datesInRange.map(date => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  return (
                    <div key={dateStr} className="flex flex-col md:flex-row items-center gap-4 bg-white/5 p-4 rounded-xl border border-white/10 group hover:border-lcars-tan/30 transition-all">
                      <div className="w-full md:w-40 font-bold text-lcars-tan text-sm uppercase">{format(date, 'EEEE, MMM dd')}</div>
                      <div className="flex flex-1 items-center gap-3 w-full">
                         <div className="flex-1 space-y-1">
                           <label htmlFor={`wiz-open-${dateStr}`} className="text-[0.6rem] text-[var(--foreground)]/40 font-bold uppercase block ml-2">Open</label>
                           <Input 
                             id={`wiz-open-${dateStr}`}
                             type="time" 
                             className="bg-[var(--background)]/50 border-lcars-tan h-10 w-full text-[var(--foreground)]" 
                             onChange={(e) => setMetadata({
                               ...metadata, 
                               dailyHours: { 
                                 ...metadata.dailyHours, 
                                 [dateStr]: { ...metadata.dailyHours?.[dateStr], open: e.target.value } 
                               }
                             })}
                           />
                         </div>
                         <div className="flex-1 space-y-1">
                           <label htmlFor={`wiz-close-${dateStr}`} className="text-[0.6rem] text-[var(--foreground)]/40 font-bold uppercase block ml-2">Close</label>
                           <Input 
                             id={`wiz-close-${dateStr}`}
                             type="time" 
                             className="bg-[var(--background)]/50 border-lcars-tan h-10 w-full text-[var(--foreground)]" 
                             onChange={(e) => setMetadata({
                               ...metadata, 
                               dailyHours: { 
                                 ...metadata.dailyHours, 
                                 [dateStr]: { ...metadata.dailyHours?.[dateStr], close: e.target.value } 
                               }
                             })}
                           />
                         </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="bg-lcars-blue/10 p-4 rounded-lg flex items-start gap-3 mt-8">
                 <div className="mt-1"><Clock className="w-4 h-4 text-lcars-blue" /></div>
                 <p className="text-[0.65rem] text-lcars-blue font-bold uppercase leading-relaxed italic opacity-80">
                   Attention: Shift generation occurs in the Tactical Command Deck after convention registration is confirmed.
                 </p>
              </div>
            </div>

            <div className="flex justify-between items-center pt-8 border-t border-white/10">
              <button 
                onClick={() => setStep(2)}
                className="lcars-pill bg-lcars-tan text-black flex items-center gap-2 px-8 py-3 font-bold uppercase hover:bg-white transition-all"
              >
                <ChevronLeft className="w-5 h-5" /> SCENE RESERVATION
              </button>
              <button 
                onClick={handleFinalize}
                disabled={loading}
                className="lcars-pill bg-lcars-blue text-black flex items-center gap-2 px-12 py-3 font-bold uppercase transition-all hover:bg-white"
              >
                {loading ? 'GENERATING...' : <><Save className="w-5 h-5" /> INITIALIZE CONVENTION</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
